/**
 * Admin site-settings tab components for homepage sub-sections + Contact page.
 *
 * Each tab receives:
 *   - settings: full site_settings object
 *   - update(path, value): dot-path setter into the settings object
 *   - openMediaPicker(path): opens the shared MediaPicker and writes into `path`
 *
 * These components ONLY edit text/link/image fields on the section defaults.
 * The card counts are fixed (icons are position-mapped in the render layer),
 * so cards can only be edited, not added/removed.
 */
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Image as ImageIcon } from "lucide-react";
import {
  SOLUTIONS_OVERVIEW_DEFAULT,
  WHY_ANTERA_DEFAULT,
  PRODUCTS_SHOWCASE_DEFAULT,
  FEATURES_GRID_DEFAULT,
  SUCCESS_STORY_DEFAULT,
  FINAL_CTA_DEFAULT,
  CTA_BANNER_DARK_DEFAULT,
  CTA_BANNER_LIGHT_DEFAULT,
  CONTACT_PAGE_DEFAULT,
  PRESENTATION_FEATURE_DEFAULT,
} from "@/data/homepageSectionDefaults";

/* ---------- Shared UI helpers ---------- */

export function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
        {label}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
      <div>
        <h2 className="text-sm font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function EditableImage({ url, onChange, onPick, label = "Image" }) {
  return (
    <Field label={label}>
      <div className="flex items-start gap-3">
        {url && (
          <img
            src={url}
            alt=""
            className="w-32 h-20 object-cover rounded-lg border border-slate-200"
          />
        )}
        <div className="flex-1 space-y-2">
          <Button
            type="button"
            onClick={onPick}
            variant="outline"
            className="h-9 text-xs rounded-lg"
          >
            <ImageIcon className="h-3.5 w-3.5 mr-1" />
            {url ? "Change image" : "Pick from library"}
          </Button>
          <Input
            value={url || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://..."
            className="text-xs font-mono"
          />
        </div>
      </div>
    </Field>
  );
}

/* ---------- Section: Solutions Overview ---------- */

export function SolutionsOverviewTab({ settings, update }) {
  const c = { ...SOLUTIONS_OVERVIEW_DEFAULT, ...(settings.homepage?.solutions_overview || {}) };
  const cards = c.cards.length === SOLUTIONS_OVERVIEW_DEFAULT.cards.length
    ? c.cards
    : SOLUTIONS_OVERVIEW_DEFAULT.cards;

  const setCard = (i, patch) => {
    const next = cards.map((card, idx) => (idx === i ? { ...card, ...patch } : card));
    update("homepage.solutions_overview", { ...c, cards: next });
  };
  const setField = (key, value) =>
    update("homepage.solutions_overview", { ...c, [key]: value });

  return (
    <SectionCard
      title='"Built for your industry" section'
      subtitle="Homepage - Solutions Overview. Six industry cards. Icons stay locked, only text and link URLs are editable."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Heading">
          <Input value={c.heading} onChange={(e) => setField("heading", e.target.value)} data-testid="admin-so-heading" />
        </Field>
        <Field label="&quot;View all&quot; link label">
          <Input value={c.view_all_label} onChange={(e) => setField("view_all_label", e.target.value)} data-testid="admin-so-view-all" />
        </Field>
      </div>
      <Field label="Intro">
        <Textarea rows={2} value={c.intro} onChange={(e) => setField("intro", e.target.value)} data-testid="admin-so-intro" />
      </Field>

      <div className="space-y-3 pt-2">
        {cards.map((card, i) => (
          <div key={i} className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Card {i + 1}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input
                value={card.title}
                onChange={(e) => setCard(i, { title: e.target.value })}
                placeholder="Card title"
                className="h-9 text-xs"
                data-testid={`admin-so-card-${i}-title`}
              />
              <Input
                value={card.link}
                onChange={(e) => setCard(i, { link: e.target.value })}
                placeholder="/solutions/…"
                className="h-9 text-xs font-mono"
                data-testid={`admin-so-card-${i}-link`}
              />
            </div>
            <Textarea
              rows={2}
              value={card.desc}
              onChange={(e) => setCard(i, { desc: e.target.value })}
              placeholder="Card description"
              className="text-xs"
              data-testid={`admin-so-card-${i}-desc`}
            />
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

/* ---------- Section: Why Antera ---------- */

export function WhyAnteraTab({ settings, update }) {
  const c = { ...WHY_ANTERA_DEFAULT, ...(settings.homepage?.why_antera || {}) };
  const cards = c.cards.length === WHY_ANTERA_DEFAULT.cards.length ? c.cards : WHY_ANTERA_DEFAULT.cards;
  const trust = c.trust_bullets.length === WHY_ANTERA_DEFAULT.trust_bullets.length
    ? c.trust_bullets
    : WHY_ANTERA_DEFAULT.trust_bullets;

  const setField = (key, value) => update("homepage.why_antera", { ...c, [key]: value });
  const setCard = (i, patch) => {
    const next = cards.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update("homepage.why_antera", { ...c, cards: next });
  };
  const setBullet = (i, value) => {
    const next = trust.map((t, idx) => (idx === i ? value : t));
    update("homepage.why_antera", { ...c, trust_bullets: next });
  };

  return (
    <SectionCard title='"Why Antera?" section' subtitle="Homepage - four value cards + three trust bullets + CTA. Icons stay locked.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Eyebrow">
          <Input value={c.eyebrow} onChange={(e) => setField("eyebrow", e.target.value)} data-testid="admin-wa-eyebrow" />
        </Field>
        <div />
        <Field label="Headline lead">
          <Input value={c.heading_lead} onChange={(e) => setField("heading_lead", e.target.value)} data-testid="admin-wa-lead" />
        </Field>
        <Field label="Headline highlight (gradient)">
          <Input value={c.heading_highlight} onChange={(e) => setField("heading_highlight", e.target.value)} data-testid="admin-wa-highlight" />
        </Field>
      </div>
      <Field label="Intro paragraph">
        <Textarea rows={3} value={c.intro} onChange={(e) => setField("intro", e.target.value)} data-testid="admin-wa-intro" />
      </Field>

      <div className="space-y-3 pt-2">
        {cards.map((card, i) => (
          <div key={i} className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Card {i + 1}</div>
            <Input
              value={card.title}
              onChange={(e) => setCard(i, { title: e.target.value })}
              placeholder="Card title"
              className="h-9 text-sm"
              data-testid={`admin-wa-card-${i}-title`}
            />
            <Textarea
              rows={3}
              value={card.body}
              onChange={(e) => setCard(i, { body: e.target.value })}
              placeholder="Card body"
              className="text-xs"
              data-testid={`admin-wa-card-${i}-body`}
            />
          </div>
        ))}
      </div>

      <div className="pt-2 border-t border-slate-100">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Trust bullets</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {trust.map((t, i) => (
            <Input
              key={i}
              value={t}
              onChange={(e) => setBullet(i, e.target.value)}
              className="h-9 text-xs"
              data-testid={`admin-wa-trust-${i}`}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
        <Field label="CTA button label">
          <Input value={c.cta_label} onChange={(e) => setField("cta_label", e.target.value)} data-testid="admin-wa-cta-label" />
        </Field>
        <Field label="CTA button link">
          <Input value={c.cta_href} onChange={(e) => setField("cta_href", e.target.value)} className="font-mono text-xs" data-testid="admin-wa-cta-href" />
        </Field>
      </div>
    </SectionCard>
  );
}

/* ---------- Section: Products Showcase ---------- */

export function ProductsShowcaseTab({ settings, update, openMediaPicker }) {
  const c = { ...PRODUCTS_SHOWCASE_DEFAULT, ...(settings.homepage?.products_showcase || {}) };
  const cards = c.cards.length === PRODUCTS_SHOWCASE_DEFAULT.cards.length ? c.cards : PRODUCTS_SHOWCASE_DEFAULT.cards;

  const setField = (key, value) => update("homepage.products_showcase", { ...c, [key]: value });
  const setCard = (i, patch) => {
    const next = cards.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update("homepage.products_showcase", { ...c, cards: next });
  };

  return (
    <SectionCard title='"Our Products" section' subtitle="Homepage - Advance / Aether / Integrations cards + right-hand image. Icons stay locked.">
      <Field label="Heading">
        <Input value={c.heading} onChange={(e) => setField("heading", e.target.value)} data-testid="admin-ps-heading" />
      </Field>
      <Field label="Intro">
        <Textarea rows={2} value={c.intro} onChange={(e) => setField("intro", e.target.value)} data-testid="admin-ps-intro" />
      </Field>

      <div className="space-y-3 pt-2">
        {cards.map((card, i) => (
          <div key={i} className="bg-slate-50/60 border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Card {i + 1}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input
                value={card.title}
                onChange={(e) => setCard(i, { title: e.target.value })}
                placeholder="Product name"
                className="h-9 text-sm"
                data-testid={`admin-ps-card-${i}-title`}
              />
              <Input
                value={card.link}
                onChange={(e) => setCard(i, { link: e.target.value })}
                placeholder="/advance, /aether, ..."
                className="h-9 text-xs font-mono"
                data-testid={`admin-ps-card-${i}-link`}
              />
            </div>
            <Textarea
              rows={2}
              value={card.desc}
              onChange={(e) => setCard(i, { desc: e.target.value })}
              placeholder="Product description"
              className="text-xs"
              data-testid={`admin-ps-card-${i}-desc`}
            />
          </div>
        ))}
      </div>

      <div className="pt-2 border-t border-slate-100">
        <EditableImage
          label="Right-side product image"
          url={c.image_url}
          onChange={(v) => setField("image_url", v)}
          onPick={() => openMediaPicker("homepage.products_showcase.image_url")}
        />
      </div>
    </SectionCard>
  );
}

/* ---------- Section: "Every Step. Connected." Features workflow ---------- */

export function FeaturesGridTab({ settings, update }) {
  const c = { ...FEATURES_GRID_DEFAULT, ...(settings.homepage?.features_grid || {}) };
  const steps = Array.isArray(c.steps) && c.steps.length === FEATURES_GRID_DEFAULT.steps.length
    ? c.steps
    : FEATURES_GRID_DEFAULT.steps;
  const benefits = Array.isArray(c.benefits) && c.benefits.length === FEATURES_GRID_DEFAULT.benefits.length
    ? c.benefits
    : FEATURES_GRID_DEFAULT.benefits;

  const setField = (key, value) => update("homepage.features_grid", { ...c, [key]: value });
  const setStep = (i, patch) => {
    const next = steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    update("homepage.features_grid", { ...c, steps: next });
  };
  const setBenefit = (i, patch) => {
    const next = benefits.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
    update("homepage.features_grid", { ...c, benefits: next });
  };

  return (
    <SectionCard
      title='"Every Step. Connected." workflow'
      subtitle="Homepage - 10-step numbered workflow (CRM to Reports) + 4 benefit callouts. Numbers, icons, and colours per slot stay locked; you can edit the title and description on each step and each benefit."
    >
      <div className="grid grid-cols-1 gap-3">
        <Field label="Eyebrow">
          <Input value={c.eyebrow} onChange={(e) => setField("eyebrow", e.target.value)} data-testid="admin-fg-eyebrow" />
        </Field>
        <Field label="Heading">
          <Input value={c.heading} onChange={(e) => setField("heading", e.target.value)} data-testid="admin-fg-heading" />
        </Field>
        <Field label="Intro">
          <Textarea rows={2} value={c.intro} onChange={(e) => setField("intro", e.target.value)} data-testid="admin-fg-intro" />
        </Field>
      </div>

      <div className="pt-2 border-t border-slate-100">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">
          10 workflow steps
        </div>
        <div className="space-y-2">
          {steps.map((step, i) => (
            <div key={i} className="bg-slate-50/60 border border-slate-200 rounded-lg p-2.5">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="w-5 h-5 rounded-full bg-sky-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <Input
                  value={step.title || ""}
                  onChange={(e) => setStep(i, { title: e.target.value })}
                  placeholder="Step title"
                  className="h-8 text-sm font-semibold"
                  data-testid={`admin-fg-step-${i}-title`}
                />
              </div>
              <Textarea
                rows={2}
                value={step.desc || ""}
                onChange={(e) => setStep(i, { desc: e.target.value })}
                placeholder="Step description"
                className="text-xs"
                data-testid={`admin-fg-step-${i}-desc`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="pt-2 border-t border-slate-100">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">
          4 benefit callouts (below the workflow)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {benefits.map((b, i) => (
            <div key={i} className="bg-slate-50/60 border border-slate-200 rounded-lg p-2.5">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Benefit {i + 1}</div>
              <Input
                value={b.title || ""}
                onChange={(e) => setBenefit(i, { title: e.target.value })}
                placeholder="Benefit title"
                className="h-8 text-sm font-semibold mb-1.5"
                data-testid={`admin-fg-benefit-${i}-title`}
              />
              <Textarea
                rows={2}
                value={b.desc || ""}
                onChange={(e) => setBenefit(i, { desc: e.target.value })}
                placeholder="Benefit description"
                className="text-xs"
                data-testid={`admin-fg-benefit-${i}-desc`}
              />
            </div>
          ))}
        </div>
      </div>
    </SectionCard>
  );
}

/* ---------- Section: Success Story ---------- */

export function SuccessStoryTab({ settings, update, openMediaPicker }) {
  const c = { ...SUCCESS_STORY_DEFAULT, ...(settings.homepage?.success_story || {}) };
  const achievements = c.achievements.length === SUCCESS_STORY_DEFAULT.achievements.length
    ? c.achievements
    : SUCCESS_STORY_DEFAULT.achievements;

  const setField = (key, value) => update("homepage.success_story", { ...c, [key]: value });
  const setAchievement = (i, value) => {
    const next = achievements.map((x, idx) => (idx === i ? value : x));
    update("homepage.success_story", { ...c, achievements: next });
  };

  return (
    <SectionCard title="Success Story section" subtitle="Homepage - customer proof panel with author photo, hero image, and floating stat card.">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Badge label">
          <Input value={c.badge} onChange={(e) => setField("badge", e.target.value)} data-testid="admin-ss-badge" />
        </Field>
        <Field label="Headline lead">
          <Input value={c.headline_lead} onChange={(e) => setField("headline_lead", e.target.value)} data-testid="admin-ss-lead" />
        </Field>
        <Field label="Headline highlight (italic)">
          <Input value={c.headline_highlight} onChange={(e) => setField("headline_highlight", e.target.value)} data-testid="admin-ss-highlight" />
        </Field>
      </div>

      <div>
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Achievements (3 bullets)</div>
        <div className="space-y-2">
          {achievements.map((line, i) => (
            <Textarea
              key={i}
              rows={2}
              value={line}
              onChange={(e) => setAchievement(i, e.target.value)}
              className="text-sm"
              data-testid={`admin-ss-achievement-${i}`}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
        <Field label="Author name">
          <Input value={c.author_name} onChange={(e) => setField("author_name", e.target.value)} data-testid="admin-ss-author-name" />
        </Field>
        <Field label="Author title">
          <Input value={c.author_title} onChange={(e) => setField("author_title", e.target.value)} data-testid="admin-ss-author-title" />
        </Field>
        <div />
      </div>

      <EditableImage
        label="Author avatar"
        url={c.author_avatar}
        onChange={(v) => setField("author_avatar", v)}
        onPick={() => openMediaPicker("homepage.success_story.author_avatar")}
      />

      <EditableImage
        label="Main hero image"
        url={c.main_image}
        onChange={(v) => setField("main_image", v)}
        onPick={() => openMediaPicker("homepage.success_story.main_image")}
      />
      <Field label="Main image alt text" hint="For accessibility + SEO. Describe what's happening in the photo.">
        <Input value={c.main_image_alt} onChange={(e) => setField("main_image_alt", e.target.value)} data-testid="admin-ss-image-alt" />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2 border-t border-slate-100">
        <Field label="Floating stat number">
          <Input value={c.stat_number} onChange={(e) => setField("stat_number", e.target.value)} data-testid="admin-ss-stat-number" />
        </Field>
        <Field label="Stat label">
          <Input value={c.stat_label} onChange={(e) => setField("stat_label", e.target.value)} data-testid="admin-ss-stat-label" />
        </Field>
        <Field label="CTA button label">
          <Input value={c.cta_label} onChange={(e) => setField("cta_label", e.target.value)} data-testid="admin-ss-cta-label" />
        </Field>
        <Field label="CTA button link">
          <Input value={c.cta_href} onChange={(e) => setField("cta_href", e.target.value)} className="font-mono text-xs" data-testid="admin-ss-cta-href" />
        </Field>
      </div>
    </SectionCard>
  );
}

/* ---------- Section: CTA banners + Final CTA ---------- */

export function CTAsTab({ settings, update }) {
  const dark = { ...CTA_BANNER_DARK_DEFAULT, ...(settings.homepage?.cta_banner_dark || {}) };
  const light = { ...CTA_BANNER_LIGHT_DEFAULT, ...(settings.homepage?.cta_banner_light || {}) };
  const final_ = { ...FINAL_CTA_DEFAULT, ...(settings.homepage?.final_cta || {}) };

  const setDark = (key, value) => update("homepage.cta_banner_dark", { ...dark, [key]: value });
  const setLight = (key, value) => update("homepage.cta_banner_light", { ...light, [key]: value });
  const setFinal = (key, value) => update("homepage.final_cta", { ...final_, [key]: value });

  return (
    <div className="space-y-5">
      <SectionCard title="Dark CTA banner" subtitle="Sky-blue full-width strip. Appears after 'Why Antera'.">
        <Field label="Heading"><Input value={dark.heading} onChange={(e) => setDark("heading", e.target.value)} data-testid="admin-cta-dark-heading" /></Field>
        <Field label="Intro"><Input value={dark.intro} onChange={(e) => setDark("intro", e.target.value)} data-testid="admin-cta-dark-intro" /></Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Primary CTA label"><Input value={dark.primary_cta_label} onChange={(e) => setDark("primary_cta_label", e.target.value)} data-testid="admin-cta-dark-primary-label" /></Field>
          <Field label="Primary CTA link"><Input value={dark.primary_cta_href} onChange={(e) => setDark("primary_cta_href", e.target.value)} className="font-mono text-xs" data-testid="admin-cta-dark-primary-href" /></Field>
          <Field label="Secondary CTA label"><Input value={dark.secondary_cta_label} onChange={(e) => setDark("secondary_cta_label", e.target.value)} data-testid="admin-cta-dark-secondary-label" /></Field>
          <Field label="Secondary CTA link"><Input value={dark.secondary_cta_href} onChange={(e) => setDark("secondary_cta_href", e.target.value)} className="font-mono text-xs" data-testid="admin-cta-dark-secondary-href" /></Field>
        </div>
      </SectionCard>

      <SectionCard title="Light CTA banner" subtitle="Neutral strip between Success Story and Testimonials.">
        <Field label="Heading"><Input value={light.heading} onChange={(e) => setLight("heading", e.target.value)} data-testid="admin-cta-light-heading" /></Field>
        <Field label="Intro"><Input value={light.intro} onChange={(e) => setLight("intro", e.target.value)} data-testid="admin-cta-light-intro" /></Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="CTA button label"><Input value={light.cta_label} onChange={(e) => setLight("cta_label", e.target.value)} data-testid="admin-cta-light-label" /></Field>
          <Field label="CTA button link"><Input value={light.cta_href} onChange={(e) => setLight("cta_href", e.target.value)} className="font-mono text-xs" data-testid="admin-cta-light-href" /></Field>
        </div>
      </SectionCard>

      <SectionCard title="Final CTA (dark footer band)" subtitle="Dark navy band with 'Scale with Confidence' at the very bottom of the homepage.">
        <Field label="Heading"><Input value={final_.heading} onChange={(e) => setFinal("heading", e.target.value)} data-testid="admin-final-heading" /></Field>
        <Field label="Intro"><Textarea rows={2} value={final_.intro} onChange={(e) => setFinal("intro", e.target.value)} data-testid="admin-final-intro" /></Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Primary CTA label"><Input value={final_.primary_cta_label} onChange={(e) => setFinal("primary_cta_label", e.target.value)} data-testid="admin-final-primary-label" /></Field>
          <Field label="Primary CTA link"><Input value={final_.primary_cta_href} onChange={(e) => setFinal("primary_cta_href", e.target.value)} className="font-mono text-xs" data-testid="admin-final-primary-href" /></Field>
          <Field label="Secondary CTA label"><Input value={final_.secondary_cta_label} onChange={(e) => setFinal("secondary_cta_label", e.target.value)} data-testid="admin-final-secondary-label" /></Field>
          <Field label="Secondary CTA link"><Input value={final_.secondary_cta_href} onChange={(e) => setFinal("secondary_cta_href", e.target.value)} className="font-mono text-xs" data-testid="admin-final-secondary-href" /></Field>
        </div>
      </SectionCard>
    </div>
  );
}

/* ---------- Section: Contact Page ---------- */

export function ContactPageTab({ settings, update }) {
  const c = { ...CONTACT_PAGE_DEFAULT, ...(settings.contact_page || {}) };
  const setField = (key, value) => update("contact_page", { ...c, [key]: value });

  return (
    <SectionCard title="Contact page (/contact)" subtitle="Hero copy and success screen. Contact info (email, phone, address) is edited under the 'Social & Contact' tab and appears here too.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Hero lead (before highlight)">
          <Input value={c.hero_lead} onChange={(e) => setField("hero_lead", e.target.value)} data-testid="admin-cp-lead" />
        </Field>
        <Field label="Hero highlight (sky-blue word)">
          <Input value={c.hero_highlight} onChange={(e) => setField("hero_highlight", e.target.value)} data-testid="admin-cp-highlight" />
        </Field>
      </div>
      <Field label="Intro paragraph">
        <Textarea rows={3} value={c.intro} onChange={(e) => setField("intro", e.target.value)} data-testid="admin-cp-intro" />
      </Field>

      <div className="pt-2 border-t border-slate-100">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">
          Post-submit success screen
        </div>
        <div className="grid grid-cols-1 gap-3">
          <Field label="Success title">
            <Input value={c.success_title} onChange={(e) => setField("success_title", e.target.value)} data-testid="admin-cp-success-title" />
          </Field>
          <Field label="Success message">
            <Textarea rows={3} value={c.success_message} onChange={(e) => setField("success_message", e.target.value)} data-testid="admin-cp-success-msg" />
          </Field>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <strong>Note:</strong> The email, phone, and mailing address shown on the Contact page pull from the &ldquo;Social &amp; Contact&rdquo; tab. Update them there and both the Contact page and the site footer stay in sync.
      </div>
    </SectionCard>
  );
}


/* ---------- Section: Presentation Builder Feature (below hero) ---------- */

export function PresentationFeatureTab({ settings, update, openMediaPicker }) {
  const c = { ...PRESENTATION_FEATURE_DEFAULT, ...(settings.homepage?.presentation_feature || {}) };
  const bullets = Array.isArray(c.bullets) && c.bullets.length === PRESENTATION_FEATURE_DEFAULT.bullets.length
    ? c.bullets
    : PRESENTATION_FEATURE_DEFAULT.bullets;

  const setField = (key, value) => update("homepage.presentation_feature", { ...c, [key]: value });
  const setBullet = (i, value) => {
    const next = bullets.map((b, idx) => (idx === i ? value : b));
    update("homepage.presentation_feature", { ...c, bullets: next });
  };

  return (
    <SectionCard
      title='"Presentation Builder" featured card'
      subtitle="Card that lives in the right column of the homepage hero (directly under the top navigation). Turn it off to fall back to the dashboard image. All copy, laptop slide image, and floating annotation cards are editable."
    >
      {/* Enabled toggle */}
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={c.enabled !== false}
          onChange={(e) => setField("enabled", e.target.checked)}
          data-testid="admin-pf-enabled"
          className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
        />
        <span className="text-xs font-medium text-slate-600">Show on homepage</span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
        <Field label="Eyebrow (FEATURED pill)">
          <Input value={c.eyebrow} onChange={(e) => setField("eyebrow", e.target.value)} data-testid="admin-pf-eyebrow" />
        </Field>
        <Field label="Number of stars (0-5)">
          <Input
            type="number"
            min={0}
            max={5}
            value={c.stars}
            onChange={(e) => setField("stars", Number(e.target.value) || 0)}
            data-testid="admin-pf-stars"
          />
        </Field>
        <Field label="CTA button label">
          <Input value={c.cta_label} onChange={(e) => setField("cta_label", e.target.value)} data-testid="admin-pf-cta-label" />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Heading line 1 (dark navy)">
          <Input value={c.heading_line1} onChange={(e) => setField("heading_line1", e.target.value)} data-testid="admin-pf-heading1" />
        </Field>
        <Field label="Heading line 2 (sky blue)">
          <Input value={c.heading_line2} onChange={(e) => setField("heading_line2", e.target.value)} data-testid="admin-pf-heading2" />
        </Field>
      </div>

      <Field label="Subtitle">
        <Textarea rows={2} value={c.subtitle} onChange={(e) => setField("subtitle", e.target.value)} data-testid="admin-pf-subtitle" />
      </Field>

      {/* Bullets */}
      <div className="space-y-2 pt-1">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Checkmark bullets (4 items)</div>
        {bullets.map((b, i) => (
          <Input
            key={i}
            value={b}
            onChange={(e) => setBullet(i, e.target.value)}
            className="h-9 text-xs"
            data-testid={`admin-pf-bullet-${i}`}
          />
        ))}
      </div>

      {/* Video */}
      <div className="pt-2 border-t border-slate-100 space-y-3">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Watch Demo video</div>
        <Field label="Video URL (MP4)" hint="Defaults to the Antera Academy intro video.">
          <Input value={c.video_url} onChange={(e) => setField("video_url", e.target.value)} className="font-mono text-xs" data-testid="admin-pf-video-url" />
        </Field>
        <Field label="Video poster image URL (optional)">
          <Input value={c.video_poster || ""} onChange={(e) => setField("video_poster", e.target.value)} className="font-mono text-xs" data-testid="admin-pf-video-poster" />
        </Field>
      </div>

      {/* Secondary deep link */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
        <Field label="Secondary link label (below CTA)">
          <Input value={c.deep_link_label || ""} onChange={(e) => setField("deep_link_label", e.target.value)} data-testid="admin-pf-deep-label" />
        </Field>
        <Field label="Secondary link URL">
          <Input value={c.deep_link_href || ""} onChange={(e) => setField("deep_link_href", e.target.value)} className="font-mono text-xs" data-testid="admin-pf-deep-href" />
        </Field>
      </div>

      {/* Slide image inside laptop */}
      <div className="pt-2 border-t border-slate-100">
        <EditableImage
          url={c.slide_image}
          onChange={(v) => setField("slide_image", v || null)}
          onPick={() => openMediaPicker && openMediaPicker("homepage.presentation_feature.slide_image")}
          label="Slide image (shown inside the laptop screen)"
        />
        <p className="text-[11px] text-slate-400 mt-1">
          Leave empty to fall back to a styled &ldquo;Branded Solutions&rdquo; default slide.
        </p>
      </div>

      {/* Floating comment card */}
      <div className="pt-2 border-t border-slate-100">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Floating comment card</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Author name">
            <Input value={c.comment_author} onChange={(e) => setField("comment_author", e.target.value)} data-testid="admin-pf-comment-author" />
          </Field>
          <Field label="Timestamp">
            <Input value={c.comment_time} onChange={(e) => setField("comment_time", e.target.value)} data-testid="admin-pf-comment-time" />
          </Field>
          <Field label="Avatar image URL (optional)">
            <Input value={c.comment_avatar || ""} onChange={(e) => setField("comment_avatar", e.target.value)} className="font-mono text-xs" data-testid="admin-pf-comment-avatar" />
          </Field>
        </div>
        <Field label="Comment text">
          <Textarea rows={2} value={c.comment_text} onChange={(e) => setField("comment_text", e.target.value)} data-testid="admin-pf-comment-text" />
        </Field>
      </div>

      {/* Floating approval card */}
      <div className="pt-2 border-t border-slate-100">
        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Floating approval card</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Reviewer name">
            <Input value={c.approval_author} onChange={(e) => setField("approval_author", e.target.value)} data-testid="admin-pf-approval-author" />
          </Field>
          <Field label="Status label">
            <Input value={c.approval_status} onChange={(e) => setField("approval_status", e.target.value)} data-testid="admin-pf-approval-status" />
          </Field>
          <Field label="Timestamp">
            <Input value={c.approval_time} onChange={(e) => setField("approval_time", e.target.value)} data-testid="admin-pf-approval-time" />
          </Field>
          <Field label="Avatar image URL (optional)">
            <Input value={c.approval_avatar || ""} onChange={(e) => setField("approval_avatar", e.target.value)} className="font-mono text-xs" data-testid="admin-pf-approval-avatar" />
          </Field>
        </div>
      </div>
    </SectionCard>
  );
}
